import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, AlertCircle, CheckCircle2, FileText, Scale, Coins, Wallet } from 'lucide-react';
import { DateDisplay } from '@/shared/display/DateDisplay';
import { StatCard, PageHeader } from '@/cajita/ui';
import { getIgvMonthly, getIgvPosition, downloadPleSales, downloadPlePurchases } from './reports.api';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const TYPE_LABEL: Record<string, string> = {
  FACTURA: 'Facturas',
  BOLETA: 'Boletas',
  NOTA_CREDITO: 'Notas de crédito',
  NOTA_DEBITO: 'Notas de débito',
};

const money = (n: number | string): string =>
  'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function ReportsPage(): JSX.Element {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [downloading, setDownloading] = useState(false);
  const [downloadingP, setDownloadingP] = useState(false);

  const summary = useQuery({ queryKey: ['igv', year, month], queryFn: () => getIgvMonthly(year, month) });
  const position = useQuery({ queryKey: ['igv-position', year, month], queryFn: () => getIgvPosition(year, month) });

  const handleDownload = async (): Promise<void> => {
    setDownloading(true);
    try {
      await downloadPleSales(year, month);
    } finally {
      setDownloading(false);
    }
  };
  const handleDownloadPurchases = async (): Promise<void> => {
    setDownloadingP(true);
    try {
      await downloadPlePurchases(year, month);
    } finally {
      setDownloadingP(false);
    }
  };

  const data = summary.data;
  const dueSoon = data && Math.floor((new Date(data.dueDate).getTime() - Date.now()) / 86_400_000) < 7;

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title="Reportes"
          sub="IGV del periodo (ventas − compras) y PLE: Registro de Ventas (14.1) y Compras (8.1)"
          actions={
            <>
              <select className="select" style={{ width: 'auto' }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
              <select className="select" style={{ width: 'auto' }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {[year - 2, year - 1, year, year + 1].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </>
          }
        />

        {summary.isLoading && <div className="muted t-sm">Calculando…</div>}

        {data && (
          <div className="col gap20">
            <div className="kpi-grid">
              <StatCard label="Comprobantes emitidos" value={String(data.documentCount)} icon={<FileText size={19} />} accent="#3B82F6" />
              <StatCard label="Base imponible" value={money(data.taxableTotal)} icon={<Scale size={19} />} accent="#8B5CF6" />
              <StatCard label="IGV por pagar" value={money(data.igvTotal)} icon={<Coins size={19} />} accent="#F59E0B" sub="sin crédito de compras" />
              <StatCard label="Total facturado" value={money(data.grandTotal)} icon={<Wallet size={19} />} accent="#22C55E" />
            </div>

            {position.data && (
              <div className="card card-pad">
                <div className="card-title" style={{ marginBottom: 14 }}>
                  Posición de IGV del periodo
                </div>
                <div className="grid grid-collapse" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 16, alignItems: 'center' }}>
                  <div>
                    <div className="t-xs muted">IGV Ventas (débito fiscal)</div>
                    <div className="t-h1 tnum fw8">{money(position.data.igvVentas)}</div>
                  </div>
                  <div>
                    <div className="t-xs muted">IGV Compras (crédito fiscal)</div>
                    <div className="t-h1 tnum fw8 pos">− {money(position.data.igvCompras)}</div>
                  </div>
                  <div style={{ textAlign: 'right', borderLeft: '1px solid var(--border)', paddingLeft: 16 }}>
                    <div className="t-xs muted">{position.data.aPagar ? 'IGV a pagar' : 'Saldo a favor'}</div>
                    <div className="t-display tnum fw8" style={{ color: position.data.aPagar ? '#0E5E73' : '#4D7C0F' }}>
                      {money(Math.abs(Number(position.data.resultado)).toFixed(2))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-collapse" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="card card-pad">
                <div className="card-title" style={{ marginBottom: 14 }}>
                  Por tipo de comprobante
                </div>
                <div className="col gap10">
                  {Object.entries(data.byType).map(([type, info]) => (
                    <div key={type} className="row between aic t-sm">
                      <span className="fw6">{TYPE_LABEL[type] ?? type}</span>
                      <span className="muted">
                        {info.count} · <span className="tnum strong" style={{ color: 'var(--ink)' }}>{money(info.total)}</span>
                      </span>
                    </div>
                  ))}
                  {Object.keys(data.byType).length === 0 && <div className="muted t-sm">Sin movimientos</div>}
                </div>
              </div>

              <div className="card card-pad">
                <div className="card-title" style={{ marginBottom: 14 }}>
                  Cronograma SUNAT
                </div>
                <div className="row gap10 ait" style={{ marginBottom: 16 }}>
                  <div style={{ color: dueSoon ? 'var(--warning)' : 'var(--success)', marginTop: 2 }}>
                    {dueSoon ? <AlertCircle size={20} /> : <CheckCircle2 size={20} />}
                  </div>
                  <div>
                    <div className="t-sm">
                      Vencimiento de declaración:{' '}
                      <strong>
                        <DateDisplay value={data.dueDate} format="long" />
                      </strong>
                    </div>
                    <div className="t-xs muted" style={{ marginTop: 2 }}>
                      Calculado según el último dígito de RUC del tenant.
                    </div>
                  </div>
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                  <div className="fw6 t-sm" style={{ marginBottom: 4 }}>
                    PLE 14.1 — Registro de Ventas
                  </div>
                  <div className="t-xs muted" style={{ marginBottom: 12 }}>
                    Archivo TXT pipe-delimited según anexo SUNAT. Solo incluye comprobantes ACEPTADOS u OBSERVADOS.
                  </div>
                  <button className="btn btn-primary btn-block" onClick={handleDownload} disabled={downloading || data.documentCount === 0}>
                    <Download size={17} />
                    {downloading ? 'Generando…' : 'Descargar PLE'}
                  </button>
                </div>
              </div>
            </div>

            {position.data && (
              <div className="card card-pad">
                <div className="row between aic" style={{ marginBottom: 14 }}>
                  <div className="card-title">Registro de Compras</div>
                  <span className="t-sm muted">{position.data.compras.purchaseCount} compras</span>
                </div>
                <div className="grid grid-collapse" style={{ gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 }}>
                  <div>
                    <div className="t-xs muted">Base imponible</div>
                    <div className="t-h2 tnum fw8">{money(position.data.compras.baseImponible)}</div>
                  </div>
                  <div>
                    <div className="t-xs muted">IGV crédito fiscal</div>
                    <div className="t-h2 tnum fw8 pos">{money(position.data.compras.igvCredito)}</div>
                  </div>
                  <div>
                    <div className="t-xs muted">No gravado</div>
                    <div className="t-h2 tnum fw8">{money(position.data.compras.noGravado)}</div>
                  </div>
                  <div>
                    <div className="t-xs muted">Total compras</div>
                    <div className="t-h2 tnum fw8">{money(position.data.compras.total)}</div>
                  </div>
                </div>
                <button className="btn btn-secondary" onClick={handleDownloadPurchases} disabled={downloadingP || position.data.compras.purchaseCount === 0}>
                  <Download size={17} />
                  {downloadingP ? 'Generando…' : 'Descargar PLE 8.1 (Compras)'}
                </button>
              </div>
            )}

            <div className="card card-pad t-xs muted">
              Periodo {data.period}. Las notas de crédito restan del total. Los comprobantes anulados se excluyen del cálculo. Las cifras son referenciales — la
              declaración oficial se hace en el PDT/SOL.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
